<script setup>
import { getRequest, postRequest, putRequest } from "@/utils/request.js";
import { message } from "ant-design-vue";
import { reactive, ref, unref } from "vue";

const emit = defineEmits(["ok"]);
const title = ref("");
const open = ref(false);
const confirmLoading = ref(false);

const formModel = ref({});
const formRef = ref();
const treeList = ref([]);
const rules = reactive({
  parentId: [
    {
      required: true,
      message: "请选择上级菜单",
      trigger: "blur",
    },
  ],
  name: [
    {
      required: true,
      message: "请输入菜单/权限名称",
      trigger: "blur",
    },
  ],
  path: [
    {
      required: true,
      message: "请输入菜单/权限名称",
      trigger: "blur",
    },
  ],
  component: [
    {
      required: true,
      message: "请输入菜单/权限名称",
      trigger: "blur",
    },
  ],
  componentName: [
    {
      required: true,
      message: "请输入菜单/权限名称",
      trigger: "blur",
    },
  ],
  perms: [
    {
      required: false,
      message: "请输入菜单/权限名称",
      trigger: "blur",
    },
  ],
});

const handleAdd = (record) => {
  if (record.parentId) {
    record.type = 1;
  }
  handleEdit(record);
};

const handleEdit = (record) => {
  formModel.value = Object.assign({}, { ...record });
  open.value = true;
};

const handleClose = () => {
  open.value = false;
  formRef.value.resetFields();
};

const handleConfirm = () => {
  formRef.value.validate().then(async () => {
    const unrefFormModel = unref(formModel);
    let request;
    if (unrefFormModel.id) {
      request = putRequest;
    } else {
      request = postRequest;
    }
    request("/permission", unrefFormModel).then((response) => {
      if (response.success) {
        message.success("保存成功");
        emit("ok");
        handleClose();
      } else {
        message.error("保存失败。" + response.message);
      }
    });
  });
};

const loadData = () => {
  getRequest("/permission/treeList").then((response) => {
    if (response.success) {
      treeList.value = response.data;
    }
  });
};

loadData();

const typeChange = () => {
  if (formModel.value.type === 2) {
    rules.perms[0].required = true;
    rules.path[0].required = false;
    rules.component[0].required = false;
    rules.componentName[0].required = false;
    rules.parentId[0].required = true;
  } else if (formModel.value.type === 1) {
    rules.perms[0].required = false;
    rules.path[0].required = true;
    rules.component[0].required = true;
    rules.componentName[0].required = true;
    rules.parentId[0].required = true;
  } else {
    rules.perms[0].required = false;
    rules.path[0].required = true;
    rules.component[0].required = true;
    rules.componentName[0].required = true;
    rules.parentId[0].required = false;
  }
};

defineExpose({
  handleAdd,
  handleEdit,
  handleClose,
  title,
});
</script>

<template>
  <a-modal
    width="800px"
    :open="open"
    :title="title"
    :confirm-loading="confirmLoading"
    @cancel="handleClose"
    @ok="handleConfirm"
  >
    <a-form
      ref="formRef"
      :labelCol="{ span: 3 }"
      :wrapperCol="{ span: 24 }"
      :model="formModel"
      :rules="rules"
    >
      <a-form-item label="名称" name="name">
        <a-input
          v-model:value="formModel.name"
          placeholder="请输入菜单/权限名称"
        />
      </a-form-item>
      <a-form-item label="类型" name="type">
        <a-radio-group
          v-model:value="formModel.type"
          button-style="solid"
          @change="typeChange"
        >
          <a-radio-button :value="0">一级菜单</a-radio-button>
          <a-radio-button :value="1">子菜单</a-radio-button>
          <a-radio-button :value="2">按钮/接口权限</a-radio-button>
        </a-radio-group>
      </a-form-item>
      <template v-if="formModel.type === 1 || formModel.type === 2">
        <a-form-item label="上级菜单" name="parentId">
          <a-tree-select
            :treeData="treeList"
            v-model:value="formModel.parentId"
            :fieldNames="{
              children: 'children',
              label: 'name',
              value: 'id',
            }"
            placeholder="请选择上级菜单"
          ></a-tree-select>
        </a-form-item>
      </template>
      <template v-if="formModel.type === 0 || formModel.type === 1">
        <a-form-item label="路径" name="path">
          <a-input v-model:value="formModel.path" placeholder="请输入路径" />
        </a-form-item>
        <a-form-item label="组件" name="component">
          <a-input
            v-model:value="formModel.component"
            placeholder="请输入组件"
          />
        </a-form-item>
        <a-form-item label="组件名称" name="componentName">
          <a-input
            v-model:value="formModel.componentName"
            placeholder="请输入组件名称"
          />
        </a-form-item>
        <a-form-item label="图标" name="icon">
          <a-input v-model:value="formModel.icon" placeholder="请输入图标" />
        </a-form-item>
        <a-form-item label="是否缓存页面" name="keepAlive">
          <a-switch v-model:checked="formModel.keepAlive" />
        </a-form-item>
        <a-form-item label="是否隐藏" name="hidden">
          <a-switch v-model:checked="formModel.hidden" />
        </a-form-item>
      </template>

      <a-form-item label="权限编码" name="perms">
        <a-input v-model:value="formModel.perms" placeholder="请输入权限编码" />
      </a-form-item>
      <a-form-item label="排序" name="sort">
        <a-input-number
          v-model:value="formModel.sort"
          placeholder="请输入排序"
          style="width: 100%"
        />
      </a-form-item>

      <a-form-item label="描述" name="description">
        <a-textarea
          v-model:value="formModel.description"
          placeholder="请输入描述"
          :autoSize="{ minRows: 2, maxRows: 6 }"
        />
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<style scoped lang="scss"></style>
